import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = '/api/v1/inventory-reclassify';
const REASONS = ['Defective → Outlet', 'Customer return, cosmetic → Outlet', 'Relabel correction (wrong SKU received)'];
const STEP_LABELS = ['Old & new SKU', 'Location & quantity', 'Review', 'Confirm & run'];
const KIND_LABELS = { stage: 'Stage', take_off: 'Step 1, take off', add: 'Step 2, add', put_away: 'Put away' };

export const newRequestId = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
const money = (n) => (n == null || Number.isNaN(Number(n))
  ? '—'
  : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (value) => (value
  ? new Date(value).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—');
const usDate = (ymd) => (/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || '')) ? `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}` : ymd || '—');
const apiError = (error) => error?.response?.data?.error || error?.message || 'Request failed';

const STATUS_CHIP = {
  running: ['bg-blue-50 text-blue-700', 'Running'],
  completed: ['bg-green-50 text-green-700', 'Completed · verified'],
  failed: ['bg-gray-100 text-gray-700', 'Not done'],
  needs_checking: ['bg-amber-50 text-amber-800', 'Needs checking'],
  settled: ['bg-gray-100 text-gray-700', 'Settled by count'],
};
const STEP_DOT = { done: 'bg-green-600 text-white', not_done: 'bg-red-600 text-white', unknown: 'bg-amber-500 text-white', sending: 'bg-blue-600 text-white', pending: 'bg-gray-200 text-gray-500' };
const STEP_MARK = { done: '✓', not_done: '!', unknown: '?', sending: '…', pending: '' };

const Chip = ({ tone = 'bg-gray-100 text-gray-700', children }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{children}</span>
);
const Card = ({ title, children }) => (
  <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
    {title && <h3 className="mb-2 text-sm font-semibold text-gray-900">{title}</h3>}
    {children}
  </section>
);
const Banner = ({ tone, children }) => {
  const tones = { ok: 'border-green-200 bg-green-50 text-green-900', warn: 'border-amber-200 bg-amber-50 text-amber-900', bad: 'border-red-200 bg-red-50 text-red-900', info: 'border-blue-200 bg-blue-50 text-blue-900' };
  return <div role="status" className={`mb-3 rounded-md border p-3 text-sm ${tones[tone] || tones.info}`}>{children}</div>;
};
const Button = ({ secondary, className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`min-h-[40px] rounded-md px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${secondary ? 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50' : 'bg-gray-900 text-white hover:bg-gray-800'} ${className}`}
  />
);
const Checks = ({ checks = [] }) => (
  <ul className="space-y-1 text-sm">
    {checks.map((c) => (
      <li key={c.text} className={c.pass ? 'text-gray-800' : 'text-red-700'}>
        <span aria-hidden="true" className="mr-1">{c.pass ? '✓' : '✕'}</span>{c.text}
      </li>
    ))}
  </ul>
);
const Stepper = ({ current }) => (
  <ol className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
    {STEP_LABELS.map((label, i) => {
      const n = i + 1;
      const tone = n === current ? 'border-gray-900 text-gray-900' : n < current ? 'border-green-600 text-green-700' : 'border-gray-200 text-gray-400';
      return <li key={label} className={`border-t-2 pt-1 text-xs font-medium ${tone}`}>{n}. {label}</li>;
    })}
  </ol>
);
const Table = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm">
      <thead><tr className="border-b border-gray-200 text-xs uppercase text-gray-500">{head.map((h) => <th key={h} className="py-1 pr-3 font-medium">{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
const Td = ({ num, children, className = '' }) => <td className={`border-b border-gray-100 py-1.5 pr-3 align-top ${num ? 'text-right tabular-nums' : ''} ${className}`}>{children}</td>;

// Value for one reclassify at this moment, from its proven steps.
export const valueState = (rc) => {
  const steps = rc?.steps || [];
  const total = rc?.unit_cost ? Number(rc.unit_cost) * Number(rc.quantity) : null;
  const out = steps.some((s) => s.kind === 'take_off' && s.state === 'done');
  const inn = steps.some((s) => s.kind === 'add' && s.state === 'done');
  return { total, out, inn, inReclassification: out && !inn && rc?.status !== 'settled' };
};

const ValuePanel = ({ rc }) => {
  const v = valueState(rc);
  return (
    <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
      <div className="rounded-md bg-gray-50 p-2">{rc.source_sku} value moved out<b className="block">{v.out ? `−${money(v.total)}` : money(0)}</b></div>
      <div className="rounded-md bg-gray-50 p-2">In reclassification<b className="block">{v.inReclassification ? money(v.total) : money(0)}</b></div>
      <div className="rounded-md bg-gray-50 p-2">{rc.destination_sku} value in<b className="block">{v.inn ? `+${money(v.total)}` : money(0)}</b></div>
    </div>
  );
};

function ListScreen({ items, onNew, onOpen, canEdit, loading }) {
  return (
    <>
      <div className="mb-4 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-gray-900">Move physical units from one SKU to another and keep their history</div>
          <div className="text-sm text-gray-500">Example: defective regular stock relabeled as Outlet. Works for any compatible pair the catalog links.</div>
        </div>
        {canEdit && <Button onClick={onNew}>+ New reclassify</Button>}
      </div>
      <Card title="Reclassifies">
        {loading ? <div className="text-sm text-gray-500">Loading…</div> : !items.length ? (
          <div className="text-sm text-gray-500">No reclassifies yet.</div>
        ) : (
          <Table head={['ID', 'Old → new SKU', 'Units', 'Reason', 'By', 'Status', '']}>
            {items.map((rc) => {
              const [tone, label] = STATUS_CHIP[rc.status] || [undefined, rc.status];
              return (
                <tr key={rc.id}>
                  <Td>RCL-{rc.id}</Td>
                  <Td>{rc.source_sku} → {rc.destination_sku}</Td>
                  <Td num>{rc.quantity}</Td>
                  <Td>{rc.reason}</Td>
                  <Td>{rc.created_by || '—'}</Td>
                  <Td><Chip tone={tone}>{label}</Chip></Td>
                  <Td><Button secondary onClick={() => onOpen(rc.id)}>Open</Button></Td>
                </tr>
              );
            })}
          </Table>
        )}
        <p className="mt-2 text-xs text-gray-500">An open item blocks new reclassifies of the same SKUs until someone checks it. Each row opens one linked audit trail.</p>
      </Card>
    </>
  );
}

function SkuScreen({ form, setForm, onCancel, onNext }) {
  const [query, setQuery] = useState(form.sourceSku);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const find = useCallback(async (sku) => {
    if (!sku) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/candidates`, { params: { sku } });
      setResult(data);
      setForm((f) => ({ ...f, sourceSku: data.source.sku, sourceName: data.source.name }));
    } catch (e) { setResult(null); setError(apiError(e)); }
    setLoading(false);
  }, [setForm]);
  useEffect(() => { if (form.sourceSku) find(form.sourceSku); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Stepper current={1} />
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Old SKU (what the units are now)">
          <form onSubmit={(e) => { e.preventDefault(); find(query.trim()); }} className="flex gap-2">
            <label htmlFor="rc-source" className="sr-only">Old SKU</label>
            <input id="rc-source" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="SKU, e.g. LS-SCRM" className="min-h-[40px] min-w-0 flex-1 rounded-md border border-gray-300 px-3 text-sm" autoComplete="off" />
            <Button type="submit" disabled={!query.trim() || loading}>Find</Button>
          </form>
          {error && <div className="mt-2 text-sm text-red-700">{error}</div>}
          {result && <div className="mt-3 text-sm"><b>{result.source.sku}</b> · {result.source.name}</div>}
        </Card>
        <Card title="New SKU (what they become)">
          <div className="mb-2 text-xs text-gray-500">Only SKUs that pass every pair check can be picked. Failed checks are shown so nobody substitutes a different color, size, or a set for a part by accident.</div>
          {!result ? <div className="text-sm text-gray-500">Find the old SKU first.</div> : !result.candidates.length ? (
            <div className="text-sm text-gray-500">No SKU is linked in the Product Catalog as a condition version of {result.source.sku}. Set "Condition Version Of" on the outlet SKU first.</div>
          ) : result.candidates.map((c) => (
            <label key={c.sku} className={`mb-2 flex gap-2 rounded-md border p-2 ${form.destinationSku === c.sku ? 'border-gray-900' : 'border-gray-200'} ${c.compatible ? 'cursor-pointer' : 'opacity-70'}`}>
              <input type="radio" name="rc-destination" disabled={!c.compatible} checked={form.destinationSku === c.sku} onChange={() => setForm((f) => ({ ...f, destinationSku: c.sku }))} aria-label={c.sku} />
              <div className="min-w-0">
                <div className="font-mono text-sm">{c.sku} {c.compatible ? <Chip tone="bg-green-50 text-green-700">Compatible</Chip> : <Chip tone="bg-red-50 text-red-700">Blocked</Chip>}</div>
                <div className="text-xs text-gray-600">{c.name}</div>
                <Checks checks={c.compatible ? c.checks : c.checks.filter((x) => !x.pass)} />
              </div>
            </label>
          ))}
        </Card>
      </div>
      <div className="flex gap-2"><Button secondary onClick={onCancel}>Cancel</Button><div className="flex-1" /><Button disabled={!form.destinationSku || !result} onClick={onNext}>Next: location & quantity</Button></div>
    </>
  );
}

function QtyScreen({ form, setForm, review, onBack, onNext }) {
  const bin = review.bins.find((b) => b.location_code === form.sourceLocation);
  const qty = Number(form.quantity);
  const qtyOk = Number.isInteger(qty) && qty > 0 && bin && qty <= bin.quantity;
  const ready = qtyOk && form.destinationLocation.trim() && form.reason;
  return (
    <>
      <Stepper current={2} />
      <Card title={`Where the units are now (${form.sourceSku})`}>
        <div className="mb-2 text-xs text-gray-500">Pick the bin that was counted and relabeled, and enter the counted quantity. Quantities come from the count. Stock shown is Eadin's last ShipHero copy ({when(review.bins_synced_at)}); Eadin re-reads ShipHero right before anything moves.</div>
        {!review.bins.length ? <div className="text-sm text-gray-500">Eadin shows no {form.sourceSku} on hand in any bin.</div> : (
          <Table head={['', 'Location', 'On hand', 'Reclassify']}>
            {review.bins.map((b) => (
              <tr key={b.location_code}>
                <Td><input type="radio" name="rc-bin" aria-label={b.location_code} checked={form.sourceLocation === b.location_code} onChange={() => setForm((f) => ({ ...f, sourceLocation: b.location_code, expectedSourceBalance: b.quantity }))} /></Td>
                <Td className="font-mono">{b.location_code}</Td>
                <Td num>{b.quantity}</Td>
                <Td num>{form.sourceLocation === b.location_code ? (
                  <input aria-label="Quantity" inputMode="numeric" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value.replace(/[^0-9]/g, '') }))} className="w-24 rounded-md border border-gray-300 px-2 py-1 text-right" />
                ) : ''}</Td>
              </tr>
            ))}
          </Table>
        )}
        {bin && form.quantity !== '' && !qtyOk && <Banner tone="bad">Enter a whole number from 1 to {bin.quantity}. Recount before continuing.</Banner>}
        <div className="mt-2 text-xs text-gray-500">One bin per reclassify. Reclassify units in another bin after this one finishes.</div>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Staging location">
          <div className="text-xs text-gray-500">Units go into a reclassify-only staging location first. Nobody can pick from it and Inventory Transfer can't move stock in or out of it, so its balance only changes through this reclassify.</div>
          <div className="mt-2 font-mono text-sm">{review.staging.location_code}</div>
          {!review.staging.ready && <Banner tone="warn">{review.staging.location_code} isn't set up in ShipHero and Eadin yet. You can review, but not run.</Banner>}
        </Card>
        <Card title={`Where the ${form.destinationSku} units go`}>
          <label htmlFor="rc-dest" className="block text-xs font-medium text-gray-600">Put-away location</label>
          <input id="rc-dest" value={form.destinationLocation} onChange={(e) => setForm((f) => ({ ...f, destinationLocation: e.target.value.toUpperCase() }))} placeholder="e.g. OUTLET-A-01" className="mb-2 min-h-[40px] w-full rounded-md border border-gray-300 px-3 font-mono text-sm" autoComplete="off" />
          <label htmlFor="rc-reason" className="block text-xs font-medium text-gray-600">Reason (required)</label>
          <select id="rc-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className="mb-2 min-h-[40px] w-full rounded-md border border-gray-300 px-2 text-sm">
            <option value="">Choose a reason</option>
            {REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
          <label htmlFor="rc-note" className="block text-xs font-medium text-gray-600">Note</label>
          <input id="rc-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="mb-2 min-h-[40px] w-full rounded-md border border-gray-300 px-3 text-sm" />
          <label htmlFor="rc-counted" className="block text-xs font-medium text-gray-600">Counted by</label>
          <input id="rc-counted" value={form.countedBy} onChange={(e) => setForm((f) => ({ ...f, countedBy: e.target.value }))} className="min-h-[40px] w-full rounded-md border border-gray-300 px-3 text-sm" />
          <div className="mt-2 text-xs text-gray-500">Run by: the signed-in Eadin user. Needs the Reclassify Inventory permission.</div>
        </Card>
      </div>
      <div className="flex gap-2"><Button secondary onClick={onBack}>Back</Button><div className="flex-1" /><Button disabled={!ready} onClick={onNext}>Next: review</Button></div>
    </>
  );
}

function ReviewScreen({ form, setForm, review, onBack, onConfirm, canEdit }) {
  const { pair, bom, links, cost } = review;
  const qty = Number(form.quantity);
  const total = cost.unit_cost ? cost.unit_cost * qty : null;
  return (
    <>
      <Stepper current={3} />
      <Card title={`1. Pair check: ${pair.source.sku} → ${pair.destination.sku}`}><Checks checks={pair.checks} /></Card>
      <Card title="2. BOM inheritance">
        {bom.destination_has_bom ? (
          <Banner tone="warn">{pair.destination.sku} already has a BOM. Eadin never overwrites it here. Differences are shown; change it in Bills of Materials if needed.</Banner>
        ) : bom.copy_offered ? (
          <Banner tone="info">{pair.destination.sku} has no BOM yet. Eadin can copy {pair.source.sku}'s BOM onto it once the reclassify completes (logged in BOM history, can be rolled back).</Banner>
        ) : <Banner tone="info">Neither SKU has a BOM. Nothing to copy.</Banner>}
        {bom.rows.length > 0 && (
          <Table head={[`${pair.source.sku}`, 'Qty', `${pair.destination.sku}`, 'Qty']}>
            {bom.rows.map((r) => (
              <tr key={r.component}>
                <Td>{r.source ? r.component : '—'}</Td>
                <Td num>{r.source?.qty ?? ''}</Td>
                <Td className={!r.destination && bom.copy_offered && form.bomPlan === 'copy' ? 'text-green-700' : ''}>
                  {r.destination ? r.component : bom.copy_offered && form.bomPlan === 'copy' ? `${r.component} (copied)` : <span className="text-gray-400">missing</span>}
                </Td>
                <Td num>{r.destination?.qty ?? (bom.copy_offered && form.bomPlan === 'copy' ? r.source?.qty : '')}</Td>
              </tr>
            ))}
          </Table>
        )}
        {bom.copy_offered && (
          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            <label><input type="radio" name="rc-bom" checked={form.bomPlan === 'copy'} onChange={() => setForm((f) => ({ ...f, bomPlan: 'copy' }))} /> Copy as new version</label>
            <label><input type="radio" name="rc-bom" checked={form.bomPlan === 'none'} onChange={() => setForm((f) => ({ ...f, bomPlan: 'none' }))} /> Don't copy (Merchandising sets it later)</label>
          </div>
        )}
      </Card>
      <Card title="3. Product links: what is and isn't copied">
        <Table head={[`Link on ${pair.source.sku}`, `On ${pair.destination.sku}`, 'Why']}>
          {links.map((l) => (
            <tr key={l.link}>
              <Td>{l.link}</Td>
              <Td>{l.destination ? <Chip tone="bg-blue-50 text-blue-700">Its own: {l.destination}</Chip> : <Chip tone="bg-red-50 text-red-700">Not copied</Chip>}</Td>
              <Td>{l.why}</Td>
            </tr>
          ))}
        </Table>
      </Card>
      <Card title="4. Carried cost and original receipt history">
        <div className="mb-2 text-xs text-gray-500">The {qty} units carry {pair.source.sku}'s cost into {pair.destination.sku}. Purchase orders and receipts are never edited; the reclassify points back to them.</div>
        {!cost.unit_cost ? <Banner tone="bad">{pair.source.sku} has no cost in Eadin, so there is no cost to carry. Reclassify is blocked.</Banner> : (
          <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
            <div className="rounded-md bg-gray-50 p-2">Carried unit cost<b className="block">{money(cost.unit_cost)}</b><span className="text-xs text-gray-500">{cost.basis} cost as of {usDate(cost.as_of)}</span></div>
            <div className="rounded-md bg-gray-50 p-2">Value moved<b className="block">{money(total)}</b><span className="text-xs text-gray-500">{qty} × {money(cost.unit_cost)}</span></div>
            <div className="rounded-md bg-gray-50 p-2">Total inventory value<b className="block">No change</b><span className="text-xs text-gray-500">Moves between SKUs; any write-down is a separate Finance step</span></div>
          </div>
        )}
        <h4 className="mb-1 mt-3 text-xs font-semibold uppercase text-gray-500">Original receipts behind {pair.source.sku}'s cost</h4>
        <Table head={['Receipt', 'Units received']}>
          {cost.receipts.po_lines.map((r) => <tr key={`po-${r.po_id}`}><Td>PO {r.po_id} line</Td><Td num>{r.received}</Td></tr>)}
          {cost.receipts.containers.map((r) => <tr key={`c-${r.container_id}-${r.po_id}`}><Td>PO {r.po_id} · container {r.container_id}</Td><Td num>{r.received}</Td></tr>)}
        </Table>
        <h4 className="mb-1 mt-3 text-xs font-semibold uppercase text-gray-500">What gets recorded</h4>
        <Table head={['Event', 'SKU', 'Units', 'Value', 'Feeds']}>
          <tr><Td>Reclass out</Td><Td>{pair.source.sku}</Td><Td num>−{qty}</Td><Td num>−{money(total)}</Td><Td>{pair.source.sku} on-hand value; its unit cost doesn't change</Td></tr>
          <tr><Td>In reclassification (only while incomplete)</Td><Td>—</Td><Td num>{qty}</Td><Td num>{money(total)}</Td><Td>Month-end physical valuation shows it as its own line</Td></tr>
          <tr><Td>Reclass in</Td><Td>{pair.destination.sku}</Td><Td num>+{qty}</Td><Td num>+{money(total)}</Td><Td>{pair.destination.sku} weighted cost (like a receipt at the carried cost), profit reports, COGS when these units sell</Td></tr>
        </Table>
      </Card>
      <Card title="5. Live checks before anything moves">
        <ul className="list-disc pl-5 text-sm">
          <li>ShipHero read: {form.sourceLocation} still has exactly {form.expectedSourceBalance} {pair.source.sku}</li>
          <li>No open reclassify for {pair.source.sku} or {pair.destination.sku}</li>
          <li>{review.staging.location_code} exists in ShipHero and Eadin</li>
        </ul>
      </Card>
      {review.blockers.length > 0 && (
        <Banner tone="warn"><b>Can't run yet:</b><ul className="list-disc pl-5">{review.blockers.map((b) => <li key={b.code}>{b.text}</li>)}</ul></Banner>
      )}
      <div className="flex gap-2"><Button secondary onClick={onBack}>Back</Button><div className="flex-1" /><Button disabled={!review.can_run || !canEdit} onClick={onConfirm}>Review & confirm</Button></div>
    </>
  );
}

function ConfirmDialog({ form, review, onCancel, onRun, busy }) {
  const [typed, setTyped] = useState('');
  const { staging } = review;
  return (
    <div role="dialog" aria-modal="true" aria-label="Confirm reclassify" className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
      <div className="w-full max-w-lg rounded-lg bg-white p-4">
        <h3 className="mb-2 text-base font-semibold">Reclassify {form.quantity} × {form.sourceSku} → {form.destinationSku}?</h3>
        <ol className="list-decimal pl-5 text-sm leading-6">
          <li><b>Stage:</b> move {form.quantity} from {form.sourceLocation} into {staging.location_code}.</li>
          <li><b>Step 1, take off:</b> set {form.sourceSku} in {staging.location_code} to 0.</li>
          <li><b>Step 2, add:</b> set {form.destinationSku} in {staging.location_code} to {form.quantity}.</li>
          <li><b>Put away:</b> move {form.quantity} × {form.destinationSku} to {form.destinationLocation}.</li>
        </ol>
        <div className="mt-2 text-xs text-gray-500">Each step waits for ShipHero proof before the next. Reason: {form.reason}. BOM: {review.bom.copy_offered && form.bomPlan === 'copy' ? 'copy as new version' : 'no change'}.</div>
        <label htmlFor="rc-confirm" className="mt-3 block text-xs font-medium text-gray-600">Type the quantity to confirm</label>
        <input id="rc-confirm" inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={String(form.quantity)} className="min-h-[40px] w-full rounded-md border border-gray-300 px-3 text-sm" />
        <div className="mt-3 flex gap-2"><Button secondary onClick={onCancel}>Cancel</Button><div className="flex-1" /><Button disabled={typed !== String(form.quantity) || busy} onClick={onRun}>{busy ? 'Running…' : 'Reclassify'}</Button></div>
      </div>
    </div>
  );
}

function ResyncCost({ rc, canEdit, onReload }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const check = async () => {
    setBusy('check'); setError(''); setResult(null);
    try { setPreview((await axios.get(`${API}/${rc.id}/resync-cost`)).data); } catch (e) { setError(apiError(e)); }
    setBusy('');
  };
  const apply = async () => {
    setBusy('apply'); setError('');
    try { setResult((await axios.post(`${API}/${rc.id}/resync-cost`)).data); setPreview(null); } catch (e) { setError(apiError(e)); }
    setBusy(''); onReload();
  };
  const resyncs = rc.cost_source?.resyncs || [];
  return (
    <Card title="Re-sync cost">
      {error && <Banner tone="bad">{error}</Banner>}
      {result && (result.history?.success
        ? <Banner tone="ok">{result.changed ? <><b>Re-synced.</b> RCL-{rc.id} now carries {money(result.next.unit_cost)} a unit (was {money(result.current.unit_cost)}). {rc.destination_sku} has a new cost-history row.</> : <><b>Already current.</b> {rc.source_sku} still costs {money(result.next.unit_cost)}, so nothing changed.</>}</Banner>
        : <Banner tone="warn"><b>Cost saved on RCL-{rc.id}, but {rc.destination_sku}'s cost history wasn't written:</b> {result.history?.reason || 'unknown reason'}. Press Retry cost-history row once ShipHero is back.</Banner>)}
      <div className="mb-2 text-sm text-gray-700">Use this after a PO's freight or other costs are final. Eadin takes {rc.source_sku}'s cost again and adds a new cost-history row for {rc.destination_sku}. Nothing is sent to ShipHero and no quantity changes. Older rows, and reports for days before the re-sync, keep the old cost.</div>
      {resyncs.length > 0 && (
        <ul className="mb-2 space-y-1 text-xs text-gray-500">
          {resyncs.map((r) => <li key={r.at}>Re-synced by {r.by || '—'} on {when(r.at)}: {money(r.from?.unit_cost)} → {money(r.to?.unit_cost)} a unit, from {usDate(r.effective_ymd)}.</li>)}
        </ul>
      )}
      {preview && (
        <div className="mb-3 rounded-md border border-gray-200 p-3 text-sm">
          {preview.changed ? (
            <>
              <div>{rc.source_sku} now costs <b>{money(preview.next.unit_cost)}</b> a unit (landed {money(preview.next.landed_cost)}). RCL-{rc.id} carries <b>{money(preview.current.unit_cost)}</b> (landed {money(preview.current.landed_cost)}).</div>
              <div className="mt-1">For its {preview.units} units, {rc.destination_sku}'s value changes by <b>{money(preview.value_change)}</b>.</div>
            </>
          ) : preview.history_pending
            ? <div>RCL-{rc.id} already carries {rc.source_sku}'s current cost of {money(preview.next.unit_cost)} a unit, but {rc.destination_sku}'s cost-history row for the last re-sync was never written.</div>
            : <div>{rc.source_sku} still costs {money(preview.next.unit_cost)} a unit, the same as RCL-{rc.id} carries. Nothing to re-sync.</div>}
        </div>
      )}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button secondary disabled={!!busy} onClick={check}>{busy === 'check' ? 'Checking…' : 'Check current cost'}</Button>
          {(preview?.changed || preview?.history_pending || (result && !result.history?.success)) && (
            <Button disabled={!!busy} onClick={apply}>
              {busy === 'apply' ? 'Re-syncing…' : preview?.changed ? `Re-sync to ${money(preview.next.unit_cost)}` : 'Retry cost-history row'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function DetailScreen({ rc, canEdit, onBack, onReload, notice }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [counts, setCounts] = useState({ destination_added: '', source_in_staging: '' });
  const [note, setNote] = useState('');
  const act = async (name, body) => {
    setBusy(name); setError('');
    try { await axios.post(`${API}/${rc.id}/${name}`, body || {}); } catch (e) { setError(e?.response?.data?.code === 'needs_count' ? 'Re-send is off for this step. Count the units and record the result below.' : apiError(e)); }
    setBusy(''); onReload();
  };
  const [tone, label] = STATUS_CHIP[rc.status] || [undefined, rc.status];
  const lineage = rc.cost_source?.receipts;
  return (
    <>
      {notice && <Banner tone={notice.tone}>{notice.text}</Banner>}
      {rc.status === 'completed' && <Banner tone="ok"><b>RCL-{rc.id} completed and verified.</b> {rc.quantity} × {rc.source_sku} are now {rc.destination_sku} in {rc.destination_location}. Every step has a ShipHero change row with its tag.</Banner>}
      {rc.status === 'needs_checking' && <Banner tone="warn"><b>RCL-{rc.id} needs checking.</b> A step has no ShipHero proof yet. That doesn't mean it didn't run. While units are off {rc.source_sku} and not yet on {rc.destination_sku}, their value shows as "In reclassification". New reclassifies of these SKUs are blocked until this is settled.</Banner>}
      {rc.status === 'failed' && <Banner tone="info"><b>RCL-{rc.id} not done.</b> {rc.error_message || 'Eadin stopped before anything moved.'}</Banner>}
      <Card title={`RCL-${rc.id} · ${rc.quantity} × ${rc.source_sku} → ${rc.destination_sku}`}>
        <div className="mb-2"><Chip tone={tone}>{label}</Chip></div>
        <ol className="space-y-2">
          {(rc.steps || []).map((s) => (
            <li key={s.seq} className="flex gap-2">
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${STEP_DOT[s.state] || STEP_DOT.pending}`} aria-label={s.state}>{STEP_MARK[s.state]}</span>
              <div className="min-w-0 text-sm">
                <div><b>{KIND_LABELS[s.kind]}</b>: {s.target_balance != null ? `set ${s.sku} in ${s.to_location} to ${s.target_balance}` : `move ${s.quantity} × ${s.sku} ${s.from_location} → ${s.to_location}`} <span className="font-mono text-xs text-gray-500">{s.tag}</span></div>
                <div className="text-xs text-gray-500">
                  {s.state === 'done' && `Proof: ShipHero change row tagged ${s.tag}`}
                  {s.state === 'unknown' && `Unknown: ${s.error_message || 'no ShipHero row with this tag yet'}`}
                  {s.state === 'not_done' && `Not done: ${s.error_message || (s.evidence?.proof === 'never_sent' ? 'never sent' : 'ShipHero refused it')}`}
                  {s.state === 'pending' && 'Waiting'}
                  {s.state === 'sending' && 'Sent, waiting for ShipHero…'}
                  {s.sent_at && ` · sent ${when(s.sent_at)}`}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Card>
      <Card title="Value"><ValuePanel rc={rc} /><div className="mt-2 text-xs text-gray-500">Carried unit cost {money(rc.unit_cost)}{rc.cost_source?.as_of ? ` (${rc.cost_source.basis} as of ${usDate(rc.cost_source.as_of)})` : ''}.</div></Card>
      {['completed', 'settled'].includes(rc.status) && <ResyncCost rc={rc} canEdit={canEdit} onReload={onReload} />}
      {rc.status === 'needs_checking' && canEdit && (
        <Card title="What Eadin allows">
          {error && <Banner tone="bad">{error}</Banner>}
          <div className="mb-3 text-sm text-gray-700">Re-check reads ShipHero's log again. Re-send is allowed only for a "set to N" step, and only if every change in staging carries this reclassify's tag. Otherwise a person counts the units.</div>
          <div className="mb-4 flex flex-wrap gap-2">
            <Button secondary disabled={!!busy} onClick={() => act('recheck')}>{busy === 'recheck' ? 'Checking…' : 'Re-check ShipHero'}</Button>
            <Button disabled={!!busy} onClick={() => act('retry')}>{busy === 'retry' ? 'Working…' : 'Re-check, then re-send'}</Button>
          </div>
          <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Settle by count</h4>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs text-gray-600">{rc.destination_sku} units counted as added<input inputMode="numeric" value={counts.destination_added} onChange={(e) => setCounts((c) => ({ ...c, destination_added: e.target.value.replace(/[^0-9]/g, '') }))} className="mt-1 min-h-[40px] w-full rounded-md border border-gray-300 px-3 text-sm" /></label>
            <label className="text-xs text-gray-600">{rc.source_sku} units still in {rc.staging_location}<input inputMode="numeric" value={counts.source_in_staging} onChange={(e) => setCounts((c) => ({ ...c, source_in_staging: e.target.value.replace(/[^0-9]/g, '') }))} className="mt-1 min-h-[40px] w-full rounded-md border border-gray-300 px-3 text-sm" /></label>
          </div>
          <label className="mt-2 block text-xs text-gray-600">Note (who counted, what ShipHero shows)<input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 min-h-[40px] w-full rounded-md border border-gray-300 px-3 text-sm" /></label>
          <Button className="mt-2" disabled={!!busy || counts.destination_added === '' || counts.source_in_staging === '' || !note.trim()} onClick={() => act('settle', { counts: { destination_added: Number(counts.destination_added), source_in_staging: Number(counts.source_in_staging) }, note })}>Record count and settle</Button>
          <div className="mt-1 text-xs text-gray-500">Recording a count sends nothing to ShipHero. The outlet SKU's cost carries only the units counted as added.</div>
        </Card>
      )}
      <Card title={`Audit trail · RCL-${rc.id}`}>
        <div className="text-sm text-gray-700">Run by {rc.created_by || '—'} on {when(rc.created_at)} · counted by {rc.counted_by || '—'} · reason: {rc.reason}{rc.note ? ` · note: ${rc.note}` : ''}</div>
        <div className="text-sm text-gray-700">{rc.source_location} → {rc.staging_location} → {rc.destination_location}{rc.finished_at ? ` · finished ${when(rc.finished_at)}` : ''}</div>
        {rc.settlement && <div className="text-sm text-gray-700">Settled by {rc.settlement.by} on {when(rc.settlement.at)}: {JSON.stringify(rc.settlement.counts)} · {rc.settlement.note}</div>}
        {lineage && <div className="mt-1 text-xs text-gray-500">Cost lineage: {[...lineage.po_lines.map((l) => `PO ${l.po_id} (${l.received} received)`), ...lineage.containers.map((c) => `PO ${c.po_id} container ${c.container_id} (${c.received})`)].join('; ') || '—'}. The original receipts are unchanged.</div>}
      </Card>
      <div className="flex gap-2"><Button secondary onClick={onBack}>Back to list</Button></div>
    </>
  );
}

const EMPTY_FORM = {
  sourceSku: '', sourceName: '', destinationSku: '', sourceLocation: '', expectedSourceBalance: null, quantity: '',
  destinationLocation: '', reason: '', note: '', countedBy: '', bomPlan: 'copy',
};

export default function InventoryReclassify({ access }) {
  const canEdit = access === 'edit';
  const [screen, setScreen] = useState('list');
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [review, setReview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [requestId, setRequestId] = useState(newRequestId);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([axios.get(`${API}/status`), axios.get(API)]);
      setStatus(s.data); setItems(l.data.reclassifications || []);
    } catch (e) { setError(apiError(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = useCallback(async (id) => {
    setError('');
    try { const { data } = await axios.get(`${API}/${id}`); setDetail(data); setScreen('detail'); } catch (e) { setError(apiError(e)); }
  }, []);

  const loadReview = async () => {
    setError(''); setBusy(true);
    try {
      const { data } = await axios.get(`${API}/review`, { params: { sourceSku: form.sourceSku, destinationSku: form.destinationSku } });
      setReview(data); setScreen('qty');
    } catch (e) { setError(apiError(e)); }
    setBusy(false);
  };

  const copyBomIfStillEmpty = async () => {
    const { data } = await axios.get(`${API}/review`, { params: { sourceSku: form.sourceSku, destinationSku: form.destinationSku } });
    if (data.bom.destination_has_bom || !data.bom.source_rows.length) return { tone: 'info', text: `${form.destinationSku} BOM unchanged (it already has one).` };
    await axios.put(`/api/v1/bill-of-materials/product-ui/${data.pair.destination.id}`, { components: data.bom.source_rows });
    return { tone: 'ok', text: `BOM copied from ${form.sourceSku} to ${form.destinationSku} (in BOM history, can be rolled back).` };
  };

  const run = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(API, {
        requestId, sourceSku: form.sourceSku, destinationSku: form.destinationSku, sourceLocation: form.sourceLocation,
        destinationLocation: form.destinationLocation.trim(), quantity: Number(form.quantity), reason: form.reason,
        note: form.note || null, countedBy: form.countedBy || null, expectedSourceBalance: form.expectedSourceBalance,
      });
      let bomNotice = null;
      if (data.status === 'completed' && review.bom.copy_offered && form.bomPlan === 'copy') {
        bomNotice = await copyBomIfStillEmpty().catch((e) => ({ tone: 'bad', text: `Reclassify finished, but the BOM copy failed: ${apiError(e)}. Copy it in Bills of Materials.` }));
      }
      setNotice(bomNotice);
      setConfirming(false);
      setRequestId(newRequestId());
      await openDetail(data.id);
      loadList();
    } catch (e) {
      setConfirming(false);
      setError(e?.response?.status === 409 || e?.response?.status === 400
        ? apiError(e)
        : `${apiError(e)}. The result is unknown: open the list and check this reclassify before trying again.`);
    }
    setBusy(false);
  };

  const content = useMemo(() => {
    if (screen === 'skus') return <SkuScreen form={form} setForm={setForm} onCancel={() => setScreen('list')} onNext={loadReview} />;
    if (screen === 'qty' && review) return <QtyScreen form={form} setForm={setForm} review={review} onBack={() => setScreen('skus')} onNext={() => setScreen('review')} />;
    if (screen === 'review' && review) return <ReviewScreen form={form} setForm={setForm} review={review} canEdit={canEdit} onBack={() => setScreen('qty')} onConfirm={() => setConfirming(true)} />;
    if (screen === 'detail' && detail) return <DetailScreen rc={detail} canEdit={canEdit} notice={notice} onBack={() => { setNotice(null); setScreen('list'); loadList(); }} onReload={() => openDetail(detail.id)} />;
    return <ListScreen items={items} loading={loading} canEdit={canEdit} onOpen={openDetail} onNew={() => { setForm(EMPTY_FORM); setReview(null); setScreen('skus'); }} />;
  }, [screen, form, review, detail, items, loading, canEdit, notice]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto w-full max-w-5xl p-3 sm:p-6">
      <div className="mb-1 text-xs text-gray-500">Merchandising › Catalog Management</div>
      <h1 className="mb-3 text-lg font-semibold text-gray-900">Reclassify Inventory</h1>
      {status && !status.enabled && (
        <Banner tone="warn">Reclassify Inventory is built but switched off, so nothing can move stock yet. You can still look up pairs and review them. Staging location: {status.stagingLocation}.</Banner>
      )}
      {error && <Banner tone="bad">{error}</Banner>}
      {busy && screen === 'skus' && <div className="mb-2 text-sm text-gray-500">Loading review…</div>}
      {content}
      {confirming && review && <ConfirmDialog form={form} review={review} busy={busy} onCancel={() => setConfirming(false)} onRun={run} />}
    </div>
  );
}
